import { useState } from "react";
import { put } from "@common/api/apiShared";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useDotLegalMutation, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { useFeatureFlags, usePlanContext } from "../auth/planProvider/PlanProvider";
import { NavigationMenuItem, NavigationMenuSettingsSaveModel } from "./NavigationMenuSettings.types";
import { getNavigationMenuItemLabelKey, getNavigationMenuSubItems, getTopLevelNavigationMenuItems, navigationMenuItemDefinitions } from "./navigationMenuItems";

export interface INavigationMenuSettingsDialogProps {
    onDialogClose: () => void;
}

export function useNavigationMenuSettingsDialogHook(props: INavigationMenuSettingsDialogProps) {
    const queryClient = useDotLegalQueryClient();
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const userContext = useUserContext();
    const planFeatures = usePlanContext();
    const featureFlags = useFeatureFlags();
    const [hiddenItems, setHiddenItems] = useState<Array<NavigationMenuItem>>(userContext.hiddenNavigationMenuItems);

    const isFeatureFlagEnabled = (featureFlag: string) => !!featureFlags.isEnabled(featureFlag);
    const isAvailable = (item: NavigationMenuItem) => navigationMenuItemDefinitions[item].isAvailable(planFeatures, isFeatureFlagEnabled);

    const toMenuItemSetting = (item: NavigationMenuItem) => ({
        item,
        label: translateString(getNavigationMenuItemLabelKey(item, isFeatureFlagEnabled)),
        shown: !hiddenItems.includes(item),
    });

    const menuItemSettings = getTopLevelNavigationMenuItems(isFeatureFlagEnabled)
        .filter(isAvailable)
        .map((item) => {
            const subItems = getNavigationMenuSubItems(item).filter(isAvailable).map(toMenuItemSetting);
            const shownSubItemCount = subItems.filter((subItem) => subItem.shown).length;
            const setting = toMenuItemSetting(item);
            // A parent with sub items is only a container, shown in the menu exactly when one of its sub items is,
            // so its state is derived rather than saved.
            const shown = subItems.length > 0 ? shownSubItemCount > 0 : setting.shown;
            return { ...setting, shown, subItems, shownSubItemCount };
        });

    const previewItems = menuItemSettings
        .filter((setting) => setting.shown)
        .map((setting) => ({ ...setting, subItems: setting.subItems.filter((subItem) => subItem.shown) }));

    const visibleCount = menuItemSettings.reduce((sum, setting) => sum + (setting.shown ? 1 + setting.shownSubItemCount : 0), 0);
    const totalCount = menuItemSettings.reduce((sum, setting) => sum + 1 + setting.subItems.length, 0);
    const allShown = visibleCount === totalCount;

    // Only items without sub items are saved. Toggling a parent toggles its sub items instead, so a sub item that a
    // feature flag makes available later is shown unless someone hides it on its own.
    const onShownChange = (item: NavigationMenuItem, shown: boolean) => {
        const subItems = getNavigationMenuSubItems(item).filter(isAvailable);
        const changedItems = subItems.length > 0 ? subItems : [item];
        const withoutChangedItems = hiddenItems.filter((hiddenItem) => !changedItems.includes(hiddenItem));

        setHiddenItems(shown ? withoutChangedItems : [...withoutChangedItems, ...changedItems]);
    };

    const onToggleAll = () => {
        if (!allShown) {
            setHiddenItems([]);
            return;
        }

        const savedItems = menuItemSettings.flatMap((setting) => (setting.subItems.length > 0 ? setting.subItems : [setting]));
        setHiddenItems([...hiddenItems, ...savedItems.map((setting) => setting.item)]);
    };

    const saveMutation = useDotLegalMutation(save);
    const onSave = async () => {
        await saveMutation.mutateAsync(undefined, {
            onSuccess: () => {
                // The menu reads the hidden items off /userContext, which is cached for an hour.
                queryClient.refetchQueries("/userContext");
                props.onDialogClose();
                snackbar.show(translateString("navigationMenuSettingsSaved"));
            },
        });
    };

    function save() {
        const saveModel: NavigationMenuSettingsSaveModel = { hiddenNavigationMenuItems: hiddenItems };
        return put<{}>("/navigation-menu-settings", saveModel);
    }

    return {
        menuItemSettings,
        previewItems,
        visibleCount,
        totalCount,
        allShown,
        onShownChange,
        onToggleAll,
        onSave,
        inProgress: saveMutation.isLoading,
    };
}
